import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FileText, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";

const STATUS_COLORS = {
  new: { bg: "bg-blue-100", text: "text-blue-800", icon: Clock },
  in_progress: { bg: "bg-yellow-100", text: "text-yellow-800", icon: AlertCircle },
  completed: { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle },
  rejected: { bg: "bg-red-100", text: "text-red-800", icon: XCircle },
};

const KIND_LABELS = {
  custom_website: "Custom Website",
  premium_visibility: "Premium Visibility",
};

export default function WebsiteRequests() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (kindFilter) params.set("kind", kindFilter);
    const { data } = await api.get(`/admin/website-requests?${params}`);
    setItems(data.items || []);
  }, [statusFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (companyId, kind, newStatus) => {
    setLoading(true);
    try {
      await api.put(`/admin/website-requests/${companyId}/status`, { kind, status: newStatus });
      toast.success(`Request ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <FileText className="w-6 h-6" /> Website Requests
        </h1>
      </div>

      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="custom_website">Custom Website</option>
            <option value="premium_visibility">Premium Visibility</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {items.map((req) => {
          const statusStyle = STATUS_COLORS[req.status] || STATUS_COLORS.new;
          const StatusIcon = statusStyle.icon;
          
          return (
            <div key={req.id} className="bg-white rounded-lg border p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{req.company_name}</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                      <StatusIcon className="w-3 h-3" />
                      {req.status}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      {KIND_LABELS[req.kind]}
                    </span>
                  </div>
                  
                  {req.note && (
                    <p className="text-sm text-gray-600 mb-3">
                      <span className="font-medium">Note:</span> {req.note}
                    </p>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Requested: {new Date(req.requested_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex gap-2">
                  {req.status === "new" && (
                    <button
                      onClick={() => updateStatus(req.company_id, req.kind, "in_progress")}
                      disabled={loading}
                      className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded-lg disabled:opacity-50"
                    >
                      Start Progress
                    </button>
                  )}
                  {req.status === "in_progress" && (
                    <>
                      <button
                        onClick={() => updateStatus(req.company_id, req.kind, "completed")}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg disabled:opacity-50"
                      >
                        Complete
                      </button>
                      <button
                        onClick={() => updateStatus(req.company_id, req.kind, "rejected")}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm bg-red-100 text-red-800 hover:bg-red-200 rounded-lg disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {items.length === 0 && (
          <div className="bg-white rounded-lg border p-12 text-center text-gray-500">
            No requests found
          </div>
        )}
      </div>
    </div>
  );
}
